import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WAS_LOGGED_IN_KEY = 'rg_was_logged_in';

// URL auth yang tidak perlu di-intercept saat 401
const AUTH_URLS = ['/auth/login', '/auth/register', '/auth/me', '/auth/logout', '/auth/google'];

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const redirectToLoginExpired = () => {
  const path = window.location.pathname;
  if (path !== '/login' && path !== '/register') {
    localStorage.removeItem(WAS_LOGGED_IN_KEY);
    window.location.href = '/login?session=expired';
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);

  useEffect(() => {
    // Global axios interceptor — tangkap 401 dari API non-auth
    interceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = error.config?.url || '';
        const isAuthUrl = AUTH_URLS.some(u => url.includes(u));
        if (error.response?.status === 401 && !isAuthUrl) {
          setUser(false);
          redirectToLoginExpired();
        }
        return Promise.reject(error);
      }
    );

    checkAuth();

    // Re-check session saat user kembali ke tab (tab visibility)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkAuth();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.response.eject(interceptorRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      // Tandai bahwa user pernah login
      localStorage.setItem(WAS_LOGGED_IN_KEY, '1');
    } catch (error) {
      const status = error.response?.status;
      const wasLoggedIn = localStorage.getItem(WAS_LOGGED_IN_KEY);

      setUser(false);

      // Jika sebelumnya sudah login dan sekarang 401 → sesi expired
      if (status === 401 && wasLoggedIn) {
        redirectToLoginExpired();
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      localStorage.setItem(WAS_LOGGED_IN_KEY, '1');
      return { success: true };
    } catch (e) {
      const error = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error };
    }
  };

  const register = async (email, password, name, role = 'guru', referral_code_used = '') => {
    try {
      await axios.post(
        `${API}/auth/register`,
        { email, password, name, role, referral_code_used },
        { withCredentials: true }
      );
      return { success: true, requiresVerification: true };
    } catch (e) {
      const error = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setUser(false);
      localStorage.removeItem(WAS_LOGGED_IN_KEY);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
